import React, { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { useHubContext } from '@atlassian/company-hub-common';
import { Footer, Navbar } from '@atlassian/hub-app-shell';

interface HubShellContentComponentProps {
	editorView: EditorView;
}

/**
 * Renders the hub app shell Navbar and Footer as visual chrome inside the
 * editor content area. DOM portals are used to inject the elements directly
 * before and after the ProseMirror editable div so they occupy the same flex
 * column as the editable content. As a result:
 *
 *  - The Navbar appears above the ADF content (below the Atlaskit toolbar).
 *  - The Footer appears below the ADF content.
 *  - When the editor config panel opens on the right, all three elements
 *    (Navbar, ADF content, Footer) shrink together as one unit.
 *
 * React portals preserve the full React context tree (IntlProvider,
 * HubContext, NavigationStyleContext, etc.) even though the DOM nodes are
 * injected elsewhere.
 */
export const HubShellContentComponent = ({ editorView }: HubShellContentComponentProps) : React.ReactNode => {
	const navbarContainerRef = useRef<HTMLDivElement | null>(null);
	const footerContainerRef = useRef<HTMLDivElement | null>(null);
	const [mounted, setMounted] = useState(false);
	const { isInPreviewMode } = useHubContext();

	useEffect(() => {
		const pmDom = editorView?.dom;
		if (!pmDom) {
			return;
		}

		const pmParent = pmDom.parentElement;
		if (!pmParent) {
			return;
		}

		// Insert the Navbar container BEFORE the ProseMirror editable div.
		// Negative horizontal margins let the navbar bleed past the editor's
		// content padding so it spans the full width of the shell container.
		const navbarEl = document.createElement('div');
		navbarEl.setAttribute('data-hub-shell-navbar', 'true');
		navbarEl.style.marginLeft = '-52px';
		navbarEl.style.marginRight = '-52px';
		pmParent.insertBefore(navbarEl, pmDom);
		navbarContainerRef.current = navbarEl;

		// Insert the Footer container immediately AFTER the ProseMirror div.
		const footerEl = document.createElement('div');
		footerEl.setAttribute('data-hub-shell-footer', 'true');
		pmParent.insertBefore(footerEl, pmDom.nextSibling);
		footerContainerRef.current = footerEl;

		setMounted(true);

		return () => {
			navbarEl.remove();
			footerEl.remove();
			navbarContainerRef.current = null;
			footerContainerRef.current = null;
		};
	}, [editorView]);

	if (!mounted || !navbarContainerRef.current || !footerContainerRef.current) {
		return null;
	}

	return (
		<>
			{createPortal(
				// In preview mode the navbar should be fully interactive so authors
				// can test navigation. In edit mode pointer-events are disabled to
				// prevent accidental navigation while editing.
				<Navbar isEditMode={!isInPreviewMode} />,
				navbarContainerRef.current,
			)}
			{createPortal(<Footer />, footerContainerRef.current)}
		</>
	);
};
